import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { DialogRestaurarValoresComponent } from '../../shared/dialog-restaurar-valores/dialog-restaurar-valores.component';
import { ValidacaoResult } from '../../interfaces/validacao.interface';

/*
 * Componente base abstrato para todos os componentes de edição PQR.
 * Centraliza lógica comum de:
 * - Carregamento de dados (Padrao e Valores)
 * - Restauração de valores padrão
 * - Dialogs (PQR e Restaurar Valores)
 * - Navegação para PQR completo
 * - Tooltips
 */

@Component({
  template: '',
  standalone: true
})
export abstract class BasePqrComponent<T> {
  @ViewChild('infoTooltip') infoTooltip!: MatTooltip;
  @ViewChild('menuTooltip') menuTooltip!: MatTooltip;

  public menuTooltipVisible = false;
  public mensagensInconsistencia: string[] = [];

  // Propriedades abstratas que cada componente filho deve definir
  protected abstract valoresPadrao: T[];
  protected abstract valoresEdicao: T[];

  constructor(
    protected router: Router,
    protected toastr: ToastrService,
    protected dialogRestaurarValores: MatDialog,
    protected dialogPqr: MatDialog,
  ) {}

  // Carrega dados dos State Services.
  protected carregarDados(
    obterValoresPadrao: () => T[],
    obterValoresEdicao: () => T[]
  ): void {
    this.valoresPadrao = obterValoresPadrao();

    if (obterValoresEdicao().length === 0) {
      this.valoresEdicao = this.deepClone(this.valoresPadrao);
      return;
    }

    this.valoresEdicao = this.deepClone(obterValoresEdicao());
  }

  //Salva valores editados no State Service.
  protected abstract atualizarValores(): void;

  /**
   * Deep clone usando JSON (funcional para DTOs simples).
   * TODO: Substituir por structuredClone() quando disponível.
   */
  protected deepClone(obj: T[]): T[] {
    return JSON.parse(JSON.stringify(obj));
  }

  // Restaura valores padrão (copia *Padrao para *Valores).
  // Público para conformidade com interface ValidadorTela.
  public restaurarValoresPadrao(): void {
    this.valoresEdicao = this.deepClone(this.valoresPadrao);
    this.atualizarValores();
  }

  // Abre dialog de valores padrão do PQR.
  // Componentes filhos devem implementar para abrir seu dialog específico.
  protected abstract openDialogPqrValoresPadrao(): void;

  protected openDialogRestaurarValores(): void {
    const dialogRef = this.dialogRestaurarValores.open(
      DialogRestaurarValoresComponent,
      {
        panelClass: 'dialog-restaurar-valores',
        position: { top: '2%' },
      },
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.restaurarValoresPadrao();
        this.toastr.success('Os valores foram restaurados', 'PQR!');
      }
    });
  }


  chamarAcao(acao: string): void {
    if (acao === 'pqrDestaFuncionalidade') {
      this.pqrDestaFuncionalidade();
      return;
    }

    if (acao === 'restaurarValoresComBaseNoPqr') {
      this.restaurarValoresComBaseNoPqr();
      return;
    }

    if (acao === 'pqrCompletoEmNovaAba') {
      this.pqrCompletoEmNovaAba();
      return;
    }
  }

  // Abre dialog PQR desta funcionalidade.
  protected pqrDestaFuncionalidade(): void {
    this.openDialogPqrValoresPadrao();
  }

  // Abre dialog para restaurar valores com base no PQR.
  protected restaurarValoresComBaseNoPqr(): void {
    this.openDialogRestaurarValores();
  }

  // Abre PQR completo em nova aba.
  protected pqrCompletoEmNovaAba(): void {
    const baseUrl = window.location.href.replace(this.router.url, '');
    const url = new URL('pqr', baseUrl).href;
    window.open(url, '_blank');
  }

  openTooltips(): void {
    this.menuTooltipVisible = true;
    setTimeout(() => {
      this.infoTooltip?.show();
      this.menuTooltip?.show();
    }, 100);
  }

  closeTooltips(): void {
    this.menuTooltipVisible = false;
    setTimeout(() => {
      this.infoTooltip?.hide();
      this.menuTooltip?.hide();
    }, 100);
  }

  // Cria mensagens de inconsistência que os componentes filhos devem implementar sua lógica de validação.
  // Público pois deve ser acessível nos canDeactivate guards.
  public abstract criaMensagemInconsistencia(): string[];

  // Valida o formulário e usa criaMensagemInconsistencia() implementado pelo filho.
  validar(): ValidacaoResult {
    const mensagens = this.criaMensagemInconsistencia();
    return {
      valido: mensagens.length === 0,
      mensagens: mensagens
    };
  }
}
